import { CDM_POLICY_RULES } from "../data/cdmPolicyPack";
import { RELATED_POLICY_RULES } from "../data/regulatoryGatewayPack";
import type {
  DesignRisk,
  DutyAssessment,
  EvidenceItem,
  PolicyRule,
  Project,
  ProjectWorkspace,
  WorkspaceMetrics,
} from "../types/domain";

export const isNotifiable = (project: Project): boolean =>
  (project.workingDays ?? 0) > 30 || (project.personDays ?? 0) > 500;

export const hasMultipleContractors = (project: Project): boolean =>
  (project.contractorCount ?? 0) > 1;

const ruleIsTriggered = (rule: PolicyRule, project: Project): boolean => {
  if (rule.workTypes && !rule.workTypes.includes(project.workType)) return false;
  if (rule.regions && !rule.regions.includes(project.regulatoryRegion)) return false;

  switch (rule.trigger) {
    case "always":
      return true;
    case "design-work":
      return project.hasDesignWork;
    case "multiple-contractors":
      return hasMultipleContractors(project);
    case "notifiable-project":
      return isNotifiable(project);
    case "domestic-client":
      return project.domesticClient;
    case "chemical-use":
      return project.usesHazardousChemicals;
    case "noise-exposure":
      return project.hazardousNoisePotential;
    case "regulated-plant":
      return project.regulatedPlant;
    case "special-scheme-inspection":
      return project.regulatedPlant && project.specialSchemeInspection;
    case "major-hazard":
      return project.majorHazardPotential;
    case "reportable-event":
      return project.reportableEvent;
    case "workforce-40":
      return (project.peakWorkers ?? 0) >= 40;
  }
};

export const assessProject = (project: Project): DutyAssessment => {
  const multipleContractors = hasMultipleContractors(project);
  const notifiable = isNotifiable(project);
  const warnings: string[] = [];

  if (!project.name.trim()) warnings.push("Project title has not been confirmed.");
  if (!project.location.trim()) warnings.push("Project location has not been confirmed.");
  if (project.regulatoryRegion === "unresolved")
    warnings.push("Regulatory jurisdiction is unresolved; state-specific gateways are not shown.");
  if (project.contractorCount === null)
    warnings.push("Contractor count is unknown; principal appointments cannot be determined.");
  if (project.workingDays === null && project.personDays === null)
    warnings.push("Duration and person-days are unknown; notification status is unresolved.");
  if (!project.hasDesignWork)
    warnings.push("No design work is recorded. Confirm whether design preparation or modification occurs.");
  if (!project.regulatoryExposureConfirmed)
    warnings.push(
      "The specialist regulatory exposure screen has not been confirmed by a named project participant.",
    );
  if (
    project.workType === "new-construction" &&
    project.regulatoryRegion !== "sarawak" &&
    project.regulatoryRegion !== "unresolved"
  )
    warnings.push(
      "The selected jurisdiction has no state-specific approval overlay in this release; identify the local authority and project-specific approval pathway.",
    );

  const requiredRoles = ["Client", "Contractor"];
  if (project.hasDesignWork) requiredRoles.push("Designer");
  if (multipleContractors)
    requiredRoles.push("Principal Designer", "Principal Contractor");

  const triggeredRules = [...CDM_POLICY_RULES, ...RELATED_POLICY_RULES].filter((rule) =>
    ruleIsTriggered(rule, project),
  );
  const cdmRules = triggeredRules.filter((rule) => rule.kind === "cdm-duty");
  const relatedRules = triggeredRules.filter((rule) => rule.kind === "cross-cutting-duty");
  const approvalGateways = triggeredRules.filter((rule) => rule.kind === "approval-gateway");

  return {
    applies: true,
    notifiable,
    multipleContractors,
    triggeredRules,
    cdmRules,
    relatedRules,
    approvalGateways,
    requiredRoles,
    warnings,
  };
};

export const riskCompletionIssues = (risk: DesignRisk): string[] => {
  const issues: string[] = [];
  if (!risk.designElement.trim()) issues.push("Design element is missing.");
  if (!risk.hazard.trim()) issues.push("Hazard is missing.");
  if (!risk.affectedPeople.trim()) issues.push("Affected people are not identified.");
  if (!risk.eliminationConsidered.trim())
    issues.push("Elimination or safer design alternatives are not documented.");
  if (!risk.designDecision.trim()) issues.push("Design decision is missing.");
  if (risk.residualRisk === "not-assessed") issues.push("Residual risk is not assessed.");
  if (!risk.owner.trim()) issues.push("Risk owner is missing.");
  if (!risk.drawingReference.trim())
    issues.push("No drawing, specification or design reference is recorded.");
  if (risk.reviewStatus === "approved" && (!risk.reviewer.trim() || !risk.reviewDate))
    issues.push("Approved item is missing reviewer identity or review date.");
  return issues;
};

export const evidenceCoverage = (evidence: EvidenceItem[]): number => {
  if (evidence.length === 0) return 0;
  const weights = { missing: 0, requested: 0.25, received: 0.6, verified: 1 };
  const score = evidence.reduce((sum, item) => sum + weights[item.status], 0);
  return Math.round((score / evidence.length) * 100);
};

export const workspaceMetrics = (workspace: ProjectWorkspace): WorkspaceMetrics => {
  const assessment = assessProject(workspace.project);
  const availableEvidenceTitles = new Set(
    workspace.evidence
      .filter((item) => item.status === "received" || item.status === "verified")
      .map((item) => item.title.toLowerCase()),
  );
  const openDutyEvidence = assessment.triggeredRules.reduce(
    (count, rule) =>
      count +
      rule.expectedEvidence.filter(
        (expected) => !availableEvidenceTitles.has(expected.toLowerCase()),
      ).length,
    0,
  );
  const today = new Date().toISOString().slice(0, 10);
  const hirarcHighRisks = workspace.hirarcAssessments.filter(
    (item) => item.residualRiskScore >= 15,
  ).length;
  const controlsDue = workspace.criticalControls.filter(
    (control) =>
      control.health === "failed" ||
      control.health === "not-verified" ||
      Boolean(control.nextDueDate && control.nextDueDate < today),
  ).length;
  const openIncidents = workspace.incidents.filter(
    (incident) => incident.status !== "closed" && incident.status !== "accepted",
  ).length;
  const overdueActions =
    workspace.hirarcAssessments.filter(
      (item) => item.status !== "complete" && Boolean(item.dueDate && item.dueDate < today),
    ).length +
    workspace.dutyActions.filter(
      (action) => action.status !== "complete" && Boolean(action.dueDate && action.dueDate < today),
    ).length;

  const nonCompliantContractors = (workspace.contractors || []).filter(
    (c) => c.status === "non-compliant"
  ).length;

  const riskQuality =
    workspace.risks.length === 0
      ? 0
      : Math.round(
          (workspace.risks.filter((risk) => riskCompletionIssues(risk).length === 0).length /
            workspace.risks.length) *
            100,
        );
  const dutyCompletion =
    assessment.triggeredRules.length === 0
      ? 0
      : Math.round(
          (workspace.dutyActions.filter((item) => item.status === "complete").length /
            assessment.triggeredRules.length) *
            100,
        );
  const controlHealth =
    workspace.criticalControls.length === 0
      ? 0
      : Math.round(
          (workspace.criticalControls.filter((item) => item.health === "effective").length /
            workspace.criticalControls.length) *
            100,
        );
  const assuranceScore = Math.round(
    evidenceCoverage(workspace.evidence) * 0.3 +
      riskQuality * 0.25 +
      dutyCompletion * 0.2 +
      controlHealth * 0.25,
  );

  return {
    applicableDuties: assessment.triggeredRules.length,
    openDutyEvidence,
    riskCount: workspace.risks.length,
    unapprovedRisks: workspace.risks.filter((risk) => risk.reviewStatus !== "approved")
      .length,
    evidenceCoverage: evidenceCoverage(workspace.evidence),
    verifiedEvidence: workspace.evidence.filter((item) => item.status === "verified").length,
    hirarcHighRisks,
    controlsDue,
    openIncidents,
    overdueActions,
    nonCompliantContractors,
    assuranceScore,
  };
};
